'use client';
import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X,
  ChevronDown, ChevronUp, Info, ArrowRight, RefreshCw,
} from 'lucide-react';

// Field mapping from Excel column headers to InspectionRecord fields
const FIELD_MAP: Record<string, string> = {
  'vessel name': 'vesselName',
  'ship name': 'vesselName',
  'imo number': 'imoNumber',
  'imo no': 'imoNumber',
  'imo': 'imoNumber',
  'vessel type': 'vesselType',
  'ship type': 'vesselType',
  'flag': 'flag',
  'flag state': 'flag',
  'owner': 'owner',
  'ship owner': 'owner',
  'manager': 'manager',
  'ship manager': 'manager',
  'inspector': 'inspector',
  'inspector name': 'inspector',
  'inspector org': 'inspectorOrg',
  'inspector organisation': 'inspectorOrg',
  'inspection type': 'inspectionType',
  'type': 'inspectionType',
  'date of inspection': 'dateOfInspection',
  'inspection date': 'dateOfInspection',
  'port': 'port',
  'port of inspection': 'port',
  'country': 'country',
  'customer scope': 'customerScope',
  'scope': 'customerScope',
  'classification society': 'classificationSociety',
  'class society': 'classificationSociety',
  'deadweight': 'deadweight',
  'dwt': 'deadweight',
};

// Category code mapping from Excel section headers
const CATEGORY_MAP: Record<string, string> = {
  'classification and certification': 'CC',
  'classification & certification': 'CC',
  'hull and structure': 'HS',
  'hull & structure': 'HS',
  'mooring decks': 'MD',
  'mooring deck': 'MD',
  'weather deck and fittings': 'WD',
  'weather deck': 'WD',
  'ballast tanks and systems': 'BT',
  'ballast tanks': 'BT',
  'accommodation and crew welfare': 'AC',
  'accommodation': 'AC',
  'bridge and navigation': 'BN',
  'bridge & navigation': 'BN',
  'engine room and machinery': 'ER',
  'engine room': 'ER',
  'fire fighting': 'FF',
  'fire fighting equipment': 'FF',
  'life saving': 'LS',
  'life saving appliances': 'LS',
  'safe working practices': 'SW',
  'safe working': 'SW',
  'pollution control': 'PC',
  'pollution': 'PC',
  'operational management': 'OM',
  'operations management': 'OM',
  'cargo operations': 'VC',
  'cargo': 'VC',
};

const SEVERITY_MAP: Record<string, string> = {
  'critical': 'Critical',
  'crit': 'Critical',
  'severe': 'Severe',
  'sev': 'Severe',
  'moderate': 'Moderate',
  'mod': 'Moderate',
  'minor': 'Minor',
  'min': 'Minor',
  'normal': 'Normal',
  'n/a': 'Normal',
  'ok': 'Normal',
};

interface ParsedRow {
  ref: string;
  question: string;
  answer: string;
  severity: string;
  lsdScore: number | null;
  observation: string;
  category: string;
}

interface ParsedInspection {
  vesselDetails: Record<string, string>;
  findings: ParsedRow[];
  rawHeaders: string[];
  totalRows: number;
  mappedFields: number;
  unmappedHeaders: string[];
  warnings: string[];
}

interface ImportStep {
  id: number;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

export default function ExcelImportClient() {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedInspection | null>(null);
  const [steps, setSteps] = useState<ImportStep[]>([
    { id: 1, label: 'File selected', status: 'pending' },
    { id: 2, label: 'Parsing Excel structure', status: 'pending' },
    { id: 3, label: 'Mapping fields to checklist', status: 'pending' },
    { id: 4, label: 'Validating vessel details', status: 'pending' },
    { id: 5, label: 'Ready to import', status: 'pending' },
  ]);
  const [expandedSection, setExpandedSection] = useState<string | null>('vessel');
  const [importDone, setImportDone] = useState(false);

  const updateStep = (id: number, status: ImportStep['status']) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
  };

  const parseExcel = useCallback(async (f: File) => {
    setParsing(true);
    updateStep(1, 'done');
    updateStep(2, 'active');

    try {
      const XLSX = await import('xlsx');
      const buffer = await f.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

      updateStep(2, 'done');
      updateStep(3, 'active');

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as string[][];

      const vesselDetails: Record<string, string> = {};
      const findings: ParsedRow[] = [];
      const warnings: string[] = [];
      let headers: string[] = [];
      let headerRowIndex = -1;

      // Scan first 30 rows for vessel details (key-value pairs) and header row
      for (let i = 0; i < Math.min(30, rows.length); i++) {
        const row = rows[i];
        if (!row || row.length === 0) continue;

        const firstCell = String(row[0] || '').trim().toLowerCase();
        const secondCell = String(row[1] || '').trim();

        // Detect vessel detail rows (key: value pattern)
        if (firstCell && secondCell && FIELD_MAP[firstCell]) {
          vesselDetails[FIELD_MAP[firstCell]] = secondCell;
          continue;
        }

        // Detect header row (contains 'ref' or 'question' or 'item')
        const rowStr = row.map((c) => String(c).toLowerCase()).join(' ');
        if (
          (rowStr.includes('ref') || rowStr.includes('item no')) &&
          (rowStr.includes('question') || rowStr.includes('description') || rowStr.includes('item'))
        ) {
          headers = row.map((c) => String(c).trim());
          headerRowIndex = i;
          break;
        }
      }

      // If no explicit header row found, try to infer from first row
      if (headerRowIndex === -1 && rows.length > 0) {
        headers = rows[0].map((c) => String(c).trim());
        headerRowIndex = 0;
        warnings.push('Could not detect header row automatically — using first row as headers.');
      }

      updateStep(3, 'done');
      updateStep(4, 'active');

      // Map header columns
      const colIndex: Record<string, number> = {};
      headers.forEach((h, i) => {
        const lower = h.toLowerCase();
        if (lower.includes('ref') || lower.includes('item no')) colIndex['ref'] = i;
        else if (lower.includes('question') || lower.includes('description') || lower.includes('item')) colIndex['question'] = i;
        else if (lower.includes('answer') || lower.includes('response') || lower.includes('yes/no')) colIndex['answer'] = i;
        else if (lower.includes('severity') || lower.includes('rating')) colIndex['severity'] = i;
        else if (lower.includes('lsd') || lower.includes('score')) colIndex['lsdScore'] = i;
        else if (lower.includes('observation') || lower.includes('remark') || lower.includes('comment')) colIndex['observation'] = i;
      });

      const unmappedHeaders = headers.filter((h) => {
        const lower = h.toLowerCase();
        return h && !lower.includes('ref') && !lower.includes('question') &&
          !lower.includes('answer') && !lower.includes('severity') &&
          !lower.includes('lsd') && !lower.includes('observation') &&
          !lower.includes('item') && !lower.includes('description') &&
          !lower.includes('response') && !lower.includes('score') &&
          !lower.includes('remark') && !lower.includes('comment');
      });

      // Parse data rows
      let currentCategory = '';
      for (let i = headerRowIndex + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.every((c) => !c)) continue;

        const firstCell = String(row[0] || '').trim();
        const firstLower = firstCell.toLowerCase();

        // Detect category section headers
        const catCode = CATEGORY_MAP[firstLower];
        if (catCode) {
          currentCategory = catCode;
          continue;
        }

        // Try to infer category from ref code (e.g., "CC.01" → "CC")
        if (firstCell.match(/^[A-Z]{2}\.\d+$/)) {
          currentCategory = firstCell.split('.')[0];
        }

        const ref = colIndex['ref'] !== undefined ? String(row[colIndex['ref']] || '').trim() : firstCell;
        const question = colIndex['question'] !== undefined ? String(row[colIndex['question']] || '').trim() : '';
        const answer = colIndex['answer'] !== undefined ? String(row[colIndex['answer']] || '').trim() : '';
        const severityRaw = colIndex['severity'] !== undefined ? String(row[colIndex['severity']] || '').trim().toLowerCase() : '';
        const severity = SEVERITY_MAP[severityRaw] || (severityRaw ? severityRaw : '');
        const lsdRaw = colIndex['lsdScore'] !== undefined ? parseFloat(String(row[colIndex['lsdScore']] || '')) : NaN;
        const lsdScore = !isNaN(lsdRaw) && lsdRaw >= 1 && lsdRaw <= 5 ? lsdRaw : null;
        const observation = colIndex['observation'] !== undefined ? String(row[colIndex['observation']] || '').trim() : '';

        if (ref || question) {
          findings.push({ ref, question, answer, severity, lsdScore, observation, category: currentCategory });
        }
      }

      updateStep(4, 'done');
      updateStep(5, 'done');

      // Validate vessel details
      if (!vesselDetails['vesselName']) warnings.push('Vessel name not found — please verify after import.');
      if (!vesselDetails['imoNumber']) warnings.push('IMO number not found — please verify after import.');
      if (!vesselDetails['dateOfInspection']) warnings.push('Inspection date not found — please set manually.');

      setParsed({
        vesselDetails,
        findings,
        rawHeaders: headers,
        totalRows: findings.length,
        mappedFields: Object.keys(vesselDetails).length,
        unmappedHeaders,
        warnings,
      });

      toast.success(`Parsed ${findings.length} checklist items from "${f.name}"`);
    } catch (err) {
      console.error(err);
      updateStep(2, 'error');
      toast.error('Failed to parse Excel file. Please check the file format.');
    } finally {
      setParsing(false);
    }
  }, []);

  const handleFile = (f: File) => {
    if (!f.name.match(/\.(xlsx|xls|xlsm)$/i)) {
      toast.error('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      return;
    }
    setFile(f);
    setImportDone(false);
    setParsed(null);
    setSteps((prev) => prev.map((s) => ({ ...s, status: 'pending' })));
    parseExcel(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleConfirmImport = () => {
    // TODO: POST /api/inspections/import with parsed data
    toast.success('Inspection data imported successfully! Redirecting to Data Board...');
    setImportDone(true);
    setTimeout(() => {
      window.location.href = '/inspection-data-board';
    }, 1800);
  };

  const handleReset = () => {
    setFile(null);
    setParsed(null);
    setImportDone(false);
    setSteps((prev) => prev.map((s) => ({ ...s, status: 'pending' })));
  };

  const categoryCounts = parsed
    ? parsed.findings.reduce<Record<string, number>>((acc, f) => {
        const cat = f.category || 'Unknown';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {})
    : {};

  const findingsWithObservations = parsed ? parsed.findings.filter((f) => f.observation) : [];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-white sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Excel Import</h1>
            <p className="text-xs text-muted mt-0.5">Upload Miner Inspection Checklist files to import data into the platform</p>
          </div>
          {file && (
            <button className="btn-secondary text-xs" onClick={handleReset}>
              <RefreshCw size={14} /> New Import
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 px-6 py-6 max-w-5xl mx-auto w-full space-y-6">

        {/* Upload Zone */}
        {!file && (
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${
              dragOver ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-primary hover:bg-gray-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('excel-file-input')?.click()}
          >
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                <FileSpreadsheet size={32} style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <div>
                <p className="font-semibold text-base">Drop your inspection checklist here</p>
                <p className="text-sm text-muted mt-1">or click to browse — supports .xlsx, .xls, .xlsm</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted bg-gray-100 rounded-lg px-4 py-2">
                <Info size={12} />
                <span>Structured for Miner Inspection Checklist format. Other Excel formats will be parsed with best-effort field mapping.</span>
              </div>
            </div>
          </div>
        )}

        {/* File selected + progress */}
        {file && (
          <div className="bg-white rounded-2xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                <FileSpreadsheet size={20} style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{file.name}</p>
                <p className="text-xs text-muted">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              {!parsing && (
                <button onClick={handleReset} className="text-muted hover:text-destructive transition-colors">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {steps.map((step) => (
                <div key={`step-${step.id}`} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all ${
                    step.status === 'done' ? 'bg-green-500 text-white' :
                    step.status === 'active' ? 'bg-blue-500 text-white animate-pulse' :
                    step.status === 'error'? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {step.status === 'done' ? '✓' : step.status === 'error' ? '!' : step.id}
                  </div>
                  <span className={`text-sm ${step.status === 'done' ? 'text-green-700 font-medium' : step.status === 'active' ? 'font-medium' : 'text-muted'}`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Parsed Results */}
        {parsed && !importDone && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Checklist Items', value: parsed.totalRows, color: 'hsl(var(--primary))' },
                { label: 'Vessel Fields Mapped', value: parsed.mappedFields, color: '#16a34a' },
                { label: 'With Observations', value: findingsWithObservations.length, color: '#d97706' },
                { label: 'Categories Detected', value: Object.keys(categoryCounts).length, color: 'hsl(var(--foreground))' },
              ].map(({ label, value, color }) => (
                <div key={`card-${label}`} className="bg-white rounded-xl border p-4 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
                  <p className="text-xs text-muted mb-1">{label}</p>
                  <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
                </div>
              ))}
            </div>

            {/* Warnings */}
            {parsed.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Import Warnings</span>
                </div>
                <ul className="space-y-1">
                  {parsed.warnings.map((w, i) => (
                    <li key={`warn-${i}`} className="text-xs text-amber-700 flex items-start gap-2">
                      <span className="mt-0.5">•</span>{w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Vessel Details */}
            <div className="bg-white rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'vessel' ? null : 'vessel')}
              >
                <span className="font-semibold text-sm">Vessel Details Extracted</span>
                {expandedSection === 'vessel' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSection === 'vessel' && (
                <div className="px-5 pb-5 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    {Object.entries(parsed.vesselDetails).map(([key, val]) => (
                      <div key={`vd-${key}`} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-muted capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <p className="text-sm font-semibold mt-0.5 truncate">{val}</p>
                      </div>
                    ))}
                    {Object.keys(parsed.vesselDetails).length === 0 && (
                      <p className="text-sm text-muted col-span-3">No vessel details detected. Fields will need to be entered manually after import.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'categories' ? null : 'categories')}
              >
                <span className="font-semibold text-sm">Category Breakdown ({Object.keys(categoryCounts).length} detected)</span>
                {expandedSection === 'categories' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSection === 'categories' && (
                <div className="px-5 pb-5 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                    {Object.entries(categoryCounts).map(([cat, count]) => (
                      <div key={`cat-${cat}`} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-xs font-bold" style={{ color: 'hsl(var(--primary))' }}>{cat || '—'}</span>
                        <span className="text-xs text-muted">{count} items</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Findings Preview */}
            <div className="bg-white rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'findings' ? null : 'findings')}
              >
                <span className="font-semibold text-sm">Checklist Items Preview (first 20)</span>
                {expandedSection === 'findings' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSection === 'findings' && (
                <div className="border-t overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
                        {['Ref', 'Category', 'Question', 'Answer', 'Severity', 'LSD', 'Observation'].map((h) => (
                          <th key={`th-${h}`} className="px-3 py-2 text-left font-semibold text-muted whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.findings.slice(0, 20).map((row, i) => (
                        <tr key={`row-${i}`} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: 'hsl(var(--border))' }}>
                          <td className="px-3 py-2 font-mono font-bold text-primary whitespace-nowrap">{row.ref || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-700">{row.category || '—'}</span>
                          </td>
                          <td className="px-3 py-2 max-w-xs truncate">{row.question || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.answer || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row.severity ? (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                row.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                                row.severity === 'Severe' ? 'bg-orange-100 text-orange-700' :
                                row.severity === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                                row.severity === 'Minor'? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                              }`}>{row.severity}</span>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono">{row.lsdScore ?? '—'}</td>
                          <td className="px-3 py-2 max-w-xs truncate text-muted">{row.observation || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.findings.length > 20 && (
                    <p className="px-4 py-3 text-xs text-muted">
                      + {parsed.findings.length - 20} more items not shown in preview
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Import */}
            <div className="flex items-center justify-between bg-white rounded-2xl border shadow-card p-5" style={{ borderColor: 'hsl(var(--border))' }}>
              <div>
                <p className="font-semibold text-sm">Ready to import</p>
                <p className="text-xs text-muted mt-0.5">
                  {parsed.totalRows} checklist items · {parsed.mappedFields} vessel fields · {findingsWithObservations.length} observations
                </p>
              </div>
              <button className="btn-primary text-sm flex items-center gap-2" onClick={handleConfirmImport}>
                Confirm Import <ArrowRight size={16} />
              </button>
            </div>
          </>
        )}

        {/* Success State */}
        {importDone && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-green-600" />
            </div>
            <p className="font-bold text-lg">Import Successful</p>
            <p className="text-sm text-muted">Redirecting to Data Board...</p>
          </div>
        )}

        {/* Manual Injection Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Manual Injection Point</p>
              <p className="text-xs text-blue-700 mt-1">
                This is your manual data injection point. When you receive a completed inspection checklist from a surveyor or port captain,
                upload it here. The platform will parse the Excel structure, map all fields to the inspection record, and add it to the central database.
                For owner response reports, use the <strong>Owner Response Upload</strong> section in Follow-Up Reports.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
